import React, { useContext } from 'react';
import {
  GenericProgram,
  ProgramElement,
  WeeklyProgramQuery,
} from '@gql/generated';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import Accordion, { Head } from '@components/Accordion';
import WeeklyProgramWeekBox, {
  WeekElements,
} from './single/WeeklyProgramWeekBox';
import LanguageContext from '@lib/contexts/languageContext';
import VideosListWithIndicator from './VideosListWithIndicator';
import ChevronUp from '@assets/ChevronUp';

const Week = styled.div`
  ${Head} {
    position: sticky;
    top: 0;
    border-top: 1px solid ${({ theme }) => theme.colors.neutrals['black-2']};
    padding: 20px 15px;
    background-color: var(--bg-color-secondary);
    z-index: 5;
  }
`;

const WeekInfo = styled.div`
  position: relative;
`;

const WeekName = styled.p`
  color: #243240;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
`;

const WeekDetails = styled.p`
  color: #404040;
  font-size: 14px;
  text-transform: lowercase;
  margin: 0;
`;

const IconWrap = styled.span<{ open?: boolean }>`
  position: absolute;
  top: 50%;
  right: 20px;
  display: flex;
  height: 7px;
  width: 14px;
  transform: translateY(-50%)
    rotateX(${({ open }) => (open ? '180deg' : '0deg')});
  transform-origin: center;
  /* 500ms to match body closing timing */
  transition: transform 500ms ease;
`;

const VideosList = styled(VideosListWithIndicator)`
  background-color: var(--bg-color-primary);
`;

const ShevronIcon = styled(ChevronUp)`
  height: 100%;
  width: 100%;

  path {
    stroke: rgba(0, 0, 0, 0.75);
  }
`;

const Videos = styled.div`
  position: relative;

  .phase-videos-indicator {
    left: 18px;
  }
`;

type Props = {
  className?: string;
  //
  weekIndex: number;
  data: {
    label: string;
    slug: string;
    elements: WeekElements[];
    videosCount: number;
    duration: string;
  };
  //
  programId: string;
  stepSlug: string;
  elementSlug: string;
  isLast?: boolean;
  onOpenChange?(open: boolean): void;
  isOpen?: boolean;
  programCategory: GenericProgram['category'];
};

const CollapsableProgramWeek = (props: Props) => {
  const lang = useContext(LanguageContext);
  const { programId, weekIndex, stepSlug, elementSlug, programCategory } =
    props;

  return (
    <Week data-testid="CollapsableProgramWeek">
      <Accordion
        head={
          <WeekInfo data-testid="CollapsableProgramWeek--Head">
            <WeekName data-testid="CollapsableProgramWeek--Name">
              {props.data.label}
            </WeekName>
            <WeekDetails data-testid="CollapsableProgramWeek--Details">
              <Trans
                id="weeklyProgram.week.videos_duration"
                values={{
                  videosCount: props.data.videosCount,
                  duration: props.data.duration,
                }}
              />
            </WeekDetails>
            <IconWrap open={props.isOpen}>
              <ShevronIcon />
            </IconWrap>
          </WeekInfo>
        }
        body={
          <VideosList
            selector={`.week-${weekIndex}`}
            programCategory={programCategory}
            data-testid="CollapsableProgramWeek--Videos"
          >
            <Videos className={`week-${weekIndex}`}>
              <WeeklyProgramWeekBox
                lang={lang}
                programId={programId as string}
                weekIndex={weekIndex}
                weekSlug={props.data.slug}
                elements={props.data.elements}
                currentWeekSlug={stepSlug}
                currentElementSlug={elementSlug}
                programCategory={programCategory}
              />
            </Videos>
          </VideosList>
        }
        onOpenChange={props.onOpenChange}
        isOpen={props.isOpen}
      />
    </Week>
  );
};

export default CollapsableProgramWeek;
